<script setup>
import { onMounted, ref, onUnmounted, nextTick, computed, watch } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";
import CreateTaskDialog from "@/components/workflows/CreateTaskDialog.vue";
import StepTasksDialog from "@/components/workflows/StepTasksDialog.vue";
import ManageTaskDialog from "@/components/workflows/ManageTaskDialog.vue";
import ManageStepTasksDialog from "@/components/workflows/ManageStepTasksDialog.vue";
import StepDialog from "@/components/workflows/StepDialog.vue";

const router = useRouter();
const $loading = useLoading(loaderData);

const isStepDialogVisible = ref(false)

const workflowData = ref({
    title: "",
    description: "",
    status: "",
    requireContact: "optional",
    requireUser: "optional",
    requireLicense: "no",
    licenseNo: null,
    workflowCategoryId: null,
});

const workflowCategories = ref([]);
const licenses = ref([]);

onUnmounted(() => {
});


const route = useRoute("workflows-id");

const submitBtn = ref(1);

const toSafeString = (value) => {
    if (typeof value === "string") return value;
    if (value == null) return "";
    return String(value);
};

const toSafeArray = (value) => Array.isArray(value) ? value : [];

const normalizeWorkflowStep = (step) => ({
    ...step,
    title: toSafeString(step?.title),
    description: toSafeString(step?.description),
    conditional_trigger_summary: toSafeString(step?.conditional_trigger_summary),
    forms: toSafeArray(step?.forms),
    documents: toSafeArray(step?.documents),
    approvers: toSafeArray(step?.approvers),
    tasks: toSafeArray(step?.tasks),
});

const normalizeWorkflowSteps = (workflowSteps) => {
    if (!Array.isArray(workflowSteps)) {
        return [];
    }

    return workflowSteps
        .filter(step => step && typeof step === "object" && !Array.isArray(step))
        .map(normalizeWorkflowStep);
};

const getWorkflowData = function () {
    let id = route.params.id;
    let loader = $loading.show();
    let url = `/workflows/${id}`;
    axios
        .get(url)
        .then((response) => {
            if (response.status == "success") {
                workflowData.value = response.data.workflow;
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
        });
}

const getWorkflowCategories = () => {
    axios
        .get('/workflow-categories?all=true&status=active')
        .then((response) => {
            if (response.status === 'success') {
                workflowCategories.value = (response.data || []).map((category) => ({
                    title: category.name,
                    value: category.id,
                }));
            }
        })
        .catch(() => {});
}

const getLicenses = () => {
    axios
        .get('/licenses?per_page=100')
        .then((response) => {
            if (response.status === 'success') {
                licenses.value = (response.data.licenses?.data || []).map((license) => ({
                    title: license.number,
                    value: license.number,
                }));
            }
        })
        .catch(() => {});
}

const getWorkflowStepsData = () => {
    let loader = $loading.show();
    axios
        .get(`/workflow_steps?workflow_id=${route.params.id}`)
        .then((response) => {
            if (response.status == "success") {
                steps.value = normalizeWorkflowSteps(response.data.workflow_steps)
                fetchTaskCountsForSteps();
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
        });
}

onMounted(async () => {
    getWorkflowData();
    getWorkflowCategories();
    getLicenses();
    getWorkflowStepsData();
    getSystemFormFieldTypes();
    if(route.query.addSteps){
        nextTick(() => {
            showWorkflowInfoPanel.value = false;
            isStepDialogVisible.value = true;
        });
    }
});
const statusOptions = [
    {
        title: "Draft",
        value: "draft",
    },
    {
        title: "Approved",
        value: "approved",
    },
    {
        title: "Suspended",
        value: "suspended",
    },  
];

const RequireContactOptions = [
    {
        title: "Optional",
        value: "optional",
    },
    {
        title: "Required",
        value: "required",
    },
    {
        title: "Hidden",
        value: "hidden",
    }
];

const RequireUserOptions = [
    {
        title: "Optional",
        value: "optional",
    },
    {
        title: "Required",
        value: "required",
    },
    {
        title: "Hidden",
        value: "hidden",
    }
];

const RequireLicenseOptions = [
    {
        title: "Yes",
        value: "yes",
    },
    {
        title: "No",
        value: "no",
    }
];

const typeOptions = [
    {
        title: "Client",
        value: "client",
    },
    {
        title: "Operations",
        value: "operations",
    },  
    {
        title: "Rep",
        value: "rep",
    },    
    {
        title: "Branch Office",
        value: "branch_office",
    },      
];

const humanizeSnake = (raw) =>
    String(raw)
        .split("_")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");

const selectOptionTitle = (options, value, emptyLabel = "—") => {
    if (value == null || value === "") return emptyLabel;
    const match = options.find((opt) => String(opt.value) === String(value));
    if (match) return match.title;
    return humanizeSnake(value);
};

const workflowTypeLabel = computed(() =>
    selectOptionTitle(typeOptions, workflowData.value?.type, "Not set"),
);

const workflowRequireContactLabel = computed(() =>
    selectOptionTitle(RequireContactOptions, workflowData.value?.requireContact, "Optional"),
);

const workflowRequireUserLabel = computed(() =>
    selectOptionTitle(RequireUserOptions, workflowData.value?.requireUser, "Optional"),
);

const workflowRequireLicenseLabel = computed(() =>
    selectOptionTitle(RequireLicenseOptions, workflowData.value?.requireLicense, "No"),
);

const workflowLicenseName = computed(() => {
    const no = workflowData.value?.licenseNo;
    if (no == null || no === "") return "—";
    const match = licenses.value.find((l) => String(l.value) === String(no));
    return match ? match.title : "—";
});

const workflowStatusLabel = computed(() =>
    selectOptionTitle(statusOptions, workflowData.value?.status, "Draft"),
);

/** Exactly one panel in the right column at a time: workflow info XOR configure step */
const hasRightSidebar = computed(() => showWorkflowInfoPanel.value || isStepDialogVisible.value);

const updateWorkflow = async () => {
    let loader = $loading.show();
    submitBtn.value = 0;
    
    const payload = {
        ...workflowData.value,
    };
    
    axios
        .patch("/workflows/" + `${workflowData.value.id}`, payload)
        .then((response) => {
            if (response.status == "success") {
                toast.success(response.message, {
                    autoClose: 5000,
                });
                router.push({ name: "workflows-manage" });
            } else {
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
            submitBtn.value = 1;
        });
};


const steps = ref([])
const taskCounts = ref({})
const currentStep = ref({
    title: '',
    description: '',
    parent_id: null,
    parent_step_id: null,
    optional: false,
    forms: []
})

// make sure workflow is created before adding steps
const newStep = () => {
    mode.value = 'add'
    currentStep.value = {
        title: '',
        description: '',
        parent_id: null,
        parent_step_id: null,
        optional: false,
        forms: [],
        document_templates: []
    }
    showWorkflowInfoPanel.value = false;
    isStepDialogVisible.value = true;
}

const systemFormFieldTypes = ref([])
const getSystemFormFieldTypes = (search_query = "") => {
  axios
    .get(`/system_form_field_types`)
    .then((response) => {
      if (response.status == "success") {
        systemFormFieldTypes.value = response.data.field_types
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

const mode = ref('add')
const updateSteps = () => {
    isStepDialogVisible.value = false;
    getWorkflowStepsData();
}
const editStepIndex = ref(0)
const editStep = (step, index) => {
    mode.value = 'edit'
    editStepIndex.value = index
    currentStep.value = step
    showWorkflowInfoPanel.value = false;
    isStepDialogVisible.value = true;
}

const deleteStep = (step, index) => {
    let loader = $loading.show();
    axios
        .delete(`/workflow_steps/${step.id}`)
        .then((response) => {
            if(response.status == "success"){
                steps.value.splice(index, 1);
                delete taskCounts.value[step.id];
                toast.success(response.message, {
                    autoClose: 2000,
                });
            }else{
                toast.error(response.message, {
                    autoClose: 2000,
                });
            }
        })
        .catch((error) => {
            loader.hide();
        })
        .finally(() => {
            loader.hide();
        });
}
const showTaskDialog = ref(false);
const showStepTasksDialog = ref(false);
const showManageStepTasksDialog = ref(false);
const taskMode = ref('create');
const currentTask = ref(null);

const extractTaskCount = (workflowTasks) => {
    if (Array.isArray(workflowTasks)) return workflowTasks.length;
    if (Array.isArray(workflowTasks?.data)) return workflowTasks.data.length;
    return 0;
};

const fetchTaskCountForStep = async (stepId) => {
    if (!stepId || !route.params.id) return;
    try {
        const response = await axios.get('/workflow_tasks', {
            params: {
                workflow_id: route.params.id,
                workflow_step_id: stepId,
            }
        });
        if (response.status === 'success') {
            taskCounts.value[stepId] = extractTaskCount(response.data.workflow_tasks);
        } else {
            taskCounts.value[stepId] = 0;
        }
    } catch (error) {
        taskCounts.value[stepId] = 0;
    }
};

const fetchTaskCountsForSteps = async () => {
    if (!Array.isArray(steps.value) || steps.value.length === 0) return;
    await Promise.all(
        steps.value
            .filter(step => step?.id)
            .map(step => fetchTaskCountForStep(step.id))
    );
};

const getStepTaskCount = (step) => taskCounts.value[step?.id] ?? 0;

const addTask = (step) => {
    showTaskDialog.value = true;
    currentStep.value = step;
};

const hasTaskPermission = (permission) => {
  const userDataRaw = localStorage.getItem('userData');
  if (!userDataRaw) return false;
  const userData = JSON.parse(userDataRaw);
  const userPermissionsByRoles = userData?.permissions_by_roles || {};
  return Object.values(userPermissionsByRoles).some((permissions) => permissions.includes(permission));
};

const canViewTask = () => hasTaskPermission('View All Tasks') || hasTaskPermission('View Own Tasks');

const viewStepTasks = (step) => {
  showStepTasksDialog.value = true;
  currentStep.value = step;
};

const viewManageStepTasks = (step) => {
  showManageStepTasksDialog.value = true;
  currentStep.value = step;
};

const taskSelected = (task) => {
  console.log('Task selected:', task);
};

const taskCreated = () => {
    if (currentStep.value?.id) {
        fetchTaskCountForStep(currentStep.value.id);
        return;
    }
    fetchTaskCountsForSteps();
};

const taskUpdated = (payload) => {
    if (payload?.stepId) {
        taskCounts.value[payload.stepId] = payload.taskCount ?? taskCounts.value[payload.stepId] ?? 0;
        return;
    }
    fetchTaskCountsForSteps();
};

const discardChanges = () => {
    router.push({ name: "workflows-manage" });
};

const openStepConfig = (step, index) => {
  editStep(step, index);
};

const openAddTaskFromTree = (step) => {
  addTask(step);
};

const openViewTasksFromTree = (step) => {
  viewManageStepTasks(step);
};

/** Right column: Workflow Information — can be collapsed to focus on the step tree */
const showWorkflowInfoPanel = ref(true);

const hideWorkflowInfoPanel = () => {
  showWorkflowInfoPanel.value = false;
};

const openWorkflowInfoPanel = () => {
  showWorkflowInfoPanel.value = true;
  isStepDialogVisible.value = false;
};

watch(isStepDialogVisible, (open) => {
  if (!open) {
    showWorkflowInfoPanel.value = true;
  }
});

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div class="workflow-editor-page">
    <div
      class="workflow-grid"
      :class="{ 'workflow-grid--single-column': !hasRightSidebar }"
    >
      <VCard class="workflow-tree-card" rounded="lg">
        <VCardText class="pa-0">
          <div class="tree-header">
            <div class="tree-header-top">
              <div class="tree-header-left">
                <div class="tree-title-row d-flex align-center gap-2 flex-wrap">
                  <h2 class="tree-title">{{ workflowData.title || "Workflow" }}</h2>
                  <VChip
                    size="small"
                    :color="workflowData.status === 'approved' ? 'success' : (workflowData.status === 'suspended' ? 'warning' : 'secondary')"
                    variant="tonal"
                    class="workflow-status-chip"
                  >
                    {{ workflowStatusLabel }}
                  </VChip>
                </div>
              </div>

              <VBtn
                color="primary"
                class="flex-shrink-0"
                @click="openWorkflowInfoPanel"
              >
                <VIcon icon="tabler-edit" start />
                Edit workflow general
              </VBtn>
            </div>

            <div class="tree-meta tree-meta--subhead">
              <span>Type: <span class="tree-meta-value">{{ workflowTypeLabel }}</span></span>
              <span class="tree-meta-sep" aria-hidden="true">|</span>
              <span>Contact: <span class="tree-meta-value">{{ workflowRequireContactLabel }}</span></span>
              <span class="tree-meta-sep" aria-hidden="true">|</span>
              <span>User: <span class="tree-meta-value">{{ workflowRequireUserLabel }}</span></span>
              <span class="tree-meta-sep" aria-hidden="true">|</span>
              <span>License No: <span class="tree-meta-value">{{ workflowLicenseNo }}</span></span>
            </div>
          </div>

          <div class="tree-body">
            <div class="start-pill">Start</div>

            <template v-if="steps.length">
              <div
                v-for="(step, index) in steps"
                :key="step.id ?? index"
                class="step-wrap"
              >
                <div class="connector" />

                <VCard
                  class="step-card"
                  :class="{
                    'step-card--selected':
                      isStepDialogVisible
                      && mode === 'edit'
                      && currentStep?.id != null
                      && step.id != null
                      && Number(currentStep.id) === Number(step.id),
                  }"
                  variant="outlined"
                >
                  <VCardText class="pa-3">
                    <div class="step-header">
                      <div class="step-title-wrap d-flex align-center gap-2 min-w-0">
                        <div class="step-name text-truncate">
                          {{ step.title || "Untitled Step" }}
                        </div>
                        <VChip
                          v-if="step.optional"
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          class="flex-shrink-0"
                        >
                          Optional
                        </VChip>
                      </div>

                      <div class="step-actions d-flex justify-end gap-1">
                        <VBtn
                          icon
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          @click="openStepConfig(step, index)"
                        >
                          <VIcon icon="tabler-settings" size="16" />
                        </VBtn>

                        <VBtn
                          icon
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          @click="openAddTaskFromTree(step)"
                        >
                          <VIcon icon="tabler-checklist" size="16" />
                        </VBtn>

                        <VBtn
                          v-if="canViewTask()"
                          icon
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          @click="openViewTasksFromTree(step)"
                        >
                          <VIcon icon="tabler-eye" size="16" />
                        </VBtn>

                        <VBtn
                          icon
                          size="x-small"
                          color="error"
                          variant="tonal"
                          @click="deleteStep(step, index)"
                        >
                          <VIcon icon="tabler-trash" size="16" />
                        </VBtn>
                      </div>
                    </div>

                    <div class="step-sub mt-2">
                      <div class="step-sub-line">
                        <span>{{ step.forms?.length ? "Forms attached" : "No forms" }}</span>
                        <span class="mx-2">•</span>
                        <span>{{ step.documents?.length ? "Documents attached" : "No documents" }}</span>
                      </div>
                      <div class="step-sub-line">
                        <span>Tasks: {{ getStepTaskCount(step) }}</span>
                        <span class="mx-2">•</span>
                        <span>{{ step.direct_edit_config ? "Direct Database Edit: Yes" : "Direct Database Edit: No" }}</span>
                      </div>
                    </div>
                  </VCardText>
                </VCard>
              </div>

              <div class="add-below-wrap">
                <VBtn color="primary" variant="tonal" @click="newStep">
                  <VIcon icon="tabler-plus" start />
                  Add Step Below
                </VBtn>
              </div>
            </template>

            <template v-else>
              <div class="empty-tree">
                <p class="mb-3 text-medium-emphasis">No step yet. Start by adding first step.</p>
                <VBtn color="primary" @click="newStep">
                  <VIcon icon="tabler-plus" start />
                  Add First Step
                </VBtn>
              </div>
            </template>
          </div>
        </VCardText>
      </VCard>

      <aside
        v-if="hasRightSidebar"
        class="workflow-sidebar-column"
        aria-label="Workflow side panel"
      >
      <!-- Chỉ mount một panel: workflow info XOR configure step (không đồng thời) -->
      <VCard v-if="showWorkflowInfoPanel && !isStepDialogVisible" class="workflow-info-card" rounded="lg">
        <VCardText class="pa-5">
          <div class="workflow-info-head mb-4">
            <div class="workflow-info-title-row">
              <h3 class="workflow-info-title">Workflow Information</h3>
              <VBtn
                icon
                variant="text"
                density="comfortable"
                class="workflow-info-close"
                aria-label="Close workflow panel"
                @click="hideWorkflowInfoPanel"
              >
                <VIcon icon="tabler-x" size="20" />
              </VBtn>
            </div>
          </div>

          <VForm @submit.prevent="updateWorkflow">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="workflowData.title"
                  label="Title *"
                  placeholder="Title"
                  :rules="[requiredValidator]"
                />
              </VCol>

              <VCol cols="12" md="6">
                <AppSelect
                  v-model="workflowData.status"
                  :items="statusOptions"
                  label="Status *"
                  :rules="[requiredValidator]"
                />
              </VCol>

              <VCol cols="12" md="6">
                <AppSelect
                  v-model="workflowData.type"
                  :items="typeOptions"
                  label="Type"
                  clearable
                  placeholder="Type"
                />
              </VCol>

              <VCol cols="12" md="6">
                <AppSelect
                  v-model="workflowData.requireContact"
                  :items="RequireContactOptions"
                  label="Require Contact *"
                  :rules="[requiredValidator]"
                />
              </VCol>

              <VCol cols="12" md="6">
                <AppSelect
                  v-model="workflowData.requireUser"
                  :items="RequireUserOptions"
                  label="Require User *"
                  :rules="[requiredValidator]"
                />
              </VCol>

              <VCol cols="12" md="6">
                <AppSelect
                  v-model="workflowData.requireLicense"
                  :items="RequireLicenseOptions"
                  label="Require License *"
                  :rules="[requiredValidator]"
                />
              </VCol>

              <VCol v-if="workflowData.requireLicense === 'yes'" cols="12">
                <AppSelect
                  v-model="workflowData.licenseNo"
                  :items="licenses"
                  label="License No*"
                  clearable
                  placeholder="Select license"
                  :rules="[requiredValidator]"
                />
              </VCol>

              <VCol cols="12">
                <AppSelect
                  v-model="workflowData.workflowCategoryId"
                  :items="workflowCategories"
                  label="Category"
                  clearable
                  placeholder="Select Category"
                />
              </VCol>

              <VCol cols="12">
                <AppTextarea
                  rows="3"
                  v-model="workflowData.description"
                  label="Description"
                  placeholder="Description"
                />
              </VCol>
            </VRow>

            <div class="d-flex flex-wrap gap-2 justify-end mt-4">
              <VBtn variant="tonal" color="dark" @click.prevent="discardChanges">
                Discard
              </VBtn>
              <VBtn color="primary" @click.prevent="updateWorkflow">
                Save Workflow
              </VBtn>
            </div>
          </VForm>
        </VCardText>
      </VCard>

      <StepDialog
        v-else-if="isStepDialogVisible"
        presentation="panel"
        v-model:isDialogVisible="isStepDialogVisible"
        :system-form-field-types="systemFormFieldTypes"
        :step="currentStep"
        :steps="steps"
        :mode="mode"
        :workflow_id="workflowData.id"
        @updateSteps="updateSteps"
        @taskCreated="taskCreated"
      />
      </aside>
    </div>

    <ManageTaskDialog
      :is-dialog-visible="showTaskDialog"
      @update:is-dialog-visible="showTaskDialog = $event"
      :workflow-id="route.params.id"
      :step-id="currentStep.id"
      :mode="taskMode"
      :task="currentTask"
      @taskSelected="taskSelected"
      @taskCreated="taskCreated"
      @taskUpdated="taskUpdated"
    />

    <StepTasksDialog
      :is-dialog-visible="showStepTasksDialog"
      @update:is-dialog-visible="showStepTasksDialog = $event"
      :workflow-id="workflowData.id"
      :step-id="currentStep?.id"
      :step-title="currentStep?.title"
    />

    <ManageStepTasksDialog
      :is-dialog-visible="showManageStepTasksDialog"
      @update:is-dialog-visible="showManageStepTasksDialog = $event"
      :workflow-id="workflowData.id"
      :step-id="currentStep?.id"
      :step-title="currentStep?.title"
      @taskUpdated="taskUpdated"
    />
  </div>
</template>

<style scoped>
.workflow-editor-page {
  width: 100%;
}

.workflow-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 18px;
  align-items: start;
}

.workflow-grid--single-column {
  grid-template-columns: minmax(0, 1fr);
}

.workflow-tree-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  min-height: calc(100vh - 180px);
}

.workflow-sidebar-column {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.workflow-info-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  position: sticky;
  top: 84px;
}

.workflow-info-head {
  min-width: 0;
}

.workflow-info-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.workflow-info-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
}

.workflow-info-sub {
  padding-right: 0;
}

.workflow-info-close {
  flex-shrink: 0;
}

.tree-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.tree-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.tree-header-left {
  min-width: 0;
  flex: 1;
}

.tree-title-row {
  margin-bottom: 0;
}

.tree-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.25;
}

.workflow-status-chip {
  flex-shrink: 0;
}

.tree-meta {
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.9375rem;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}

.tree-meta--subhead {
  flex-wrap: nowrap;
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.tree-meta-value {
  color: rgb(var(--v-theme-on-surface));
  font-weight: 600;
}

.tree-meta-sep {
  color: rgba(var(--v-theme-on-surface), 0.35);
  user-select: none;
}

@media (max-width: 600px) {
  .tree-title {
    font-size: 1.45rem;
  }
}

.tree-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.start-pill {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 999px;
  padding: 8px 24px;
  font-weight: 600;
  background: #fff;
}

.step-wrap {
  width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.connector {
  width: 2px;
  height: 44px;
  background: rgba(var(--v-theme-on-surface), 0.2);
  position: relative;
  margin-bottom: 4px;
}

.connector::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -6px;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid rgba(var(--v-theme-on-surface), 0.35);
}

.step-card {
  width: 100%;
  border-radius: 10px;
}

.step-card.step-card--selected {
  background-color: rgba(139, 20, 60, 0.055);
  border-color: #8b143c !important;
  border-width: 1px;
}

.step-name {
  font-weight: 600;
  line-height: 1.2;
  min-width: 0;
  font-size: 1.1rem;
  color: #8b143c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  max-width: 100%;
}

.step-title-wrap {
  max-width: 100%;
}

.step-header {
  position: relative;
  min-height: 34px;
  padding-right: 150px;
  display: flex;
  align-items: center;
}

.step-actions {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  min-width: 136px;
}

.step-sub {
  font-size: 0.82rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

.step-sub-line + .step-sub-line {
  margin-top: 2px;
}

.add-below-wrap {
  margin-top: 18px;
}

.empty-tree {
  margin-top: 36px;
  text-align: center;
}

:deep(.v-field) {
  border-radius: 10px;
}

@media (max-width: 1280px) {
  .workflow-grid {
    grid-template-columns: 1fr;
  }

  .workflow-info-card {
    position: static;
  }

  .step-wrap {
    width: 100%;
  }
}
</style>
